import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import BusinessUnitSelector from '../../components/common/BusinessUnitsSelector';
import { useLazyGetZellerAdsHierarchyQuery } from '../../services/api/zellerAds.api';

const ZellerAdsManager = () => {
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const queryBusinessUnitId = queryParams.get('businessUnitId');
  const businessUnitId = queryBusinessUnitId ? parseInt(queryBusinessUnitId || '', 10) : null;

  const [getHierarchy, { data: hierarchyData, isLoading, error }] = useLazyGetZellerAdsHierarchyQuery();

  const updateBusinessUnitId = (value: number) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('businessUnitId', value.toString());
    navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'PAUSED':
        return 'warning';
      case 'ARCHIVED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: string | undefined, currency: string) => {
    if (!amount) return 'N/A';
    const value = parseFloat(amount) / 100; // Meta devuelve centavos
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(value);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  useEffect(() => {
    if (businessUnitId) {
      // eslint-disable-next-line no-void
      void getHierarchy({ businessUnitId });
    }
  }, [businessUnitId, getHierarchy]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Meta Ads Manager
      </Typography>

      <Box sx={{ mb: 3 }}>
        <BusinessUnitSelector businessUnitId={businessUnitId} setBusinessUnitId={updateBusinessUnitId} small />
      </Box>

      {!businessUnitId && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Selecciona una unidad de negocio para ver la estructura de Meta Ads
        </Alert>
      )}

      {businessUnitId && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {businessUnitId && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error al cargar la estructura de Meta Ads. Por favor, inténtalo de nuevo.
        </Alert>
      )}

      {businessUnitId && hierarchyData && hierarchyData.adAccounts.length > 0 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumen
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<AccountBalanceIcon />}
                  label={`${hierarchyData.stats.adAccounts} Cuentas Publicitarias`}
                  color="primary"
                />
                <Chip icon={<CampaignIcon />} label={`${hierarchyData.stats.campaigns} Campañas`} color="secondary" />
                <Chip
                  icon={<AdUnitsIcon />}
                  label={`${hierarchyData.stats.adSets} Conjuntos de Anuncios`}
                  color="info"
                />
                <Chip icon={<ImageIcon />} label={`${hierarchyData.stats.ads} Anuncios`} color="success" />
              </Box>
            </CardContent>
          </Card>

          {hierarchyData.adAccounts.map((adAccount) => (
            <Accordion key={adAccount.id} sx={{ mb: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'primary.dark' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <AccountBalanceIcon />
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {adAccount.name}
                  </Typography>
                  <Chip
                    label={adAccount.accountStatus === 1 ? 'Activa' : 'Inactiva'}
                    color={adAccount.accountStatus === 1 ? 'success' : 'error'}
                    size="small"
                  />
                  <Typography variant="body2">{adAccount.currency}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 2 }}>
                  {adAccount.campaigns.length === 0 ? (
                    <Typography color="text.secondary">No hay campañas en esta cuenta publicitaria</Typography>
                  ) : (
                    adAccount.campaigns.map((campaign) => (
                      <Accordion key={campaign.id} sx={{ mb: 1 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            backgroundColor: 'secondary.light',
                            '&:hover': { backgroundColor: 'secondary.main' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <CampaignIcon />
                            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                              {campaign.name}
                            </Typography>
                            <Chip label={campaign.status} color={getStatusColor(campaign.status)} size="small" />
                            {campaign.objective && (
                              <Typography variant="body2" color="text.secondary">
                                {campaign.objective}
                              </Typography>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ pl: 2 }}>
                            <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Detalles de la Campaña
                              </Typography>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                  gap: 1,
                                }}
                              >
                                <Typography variant="body2">
                                  <strong>Presupuesto Diario:</strong>{' '}
                                  {formatCurrency(campaign.daily_budget, adAccount.currency)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Presupuesto Total:</strong>{' '}
                                  {formatCurrency(campaign.lifetime_budget, adAccount.currency)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Fecha Inicio:</strong> {formatDate(campaign.start_time)}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Fecha Fin:</strong> {formatDate(campaign.stop_time)}
                                </Typography>
                              </Box>
                            </Box>

                            {campaign.adSets.length === 0 ? (
                              <Typography color="text.secondary">
                                No hay conjuntos de anuncios en esta campaña
                              </Typography>
                            ) : (
                              campaign.adSets.map((adSet) => (
                                <Accordion key={adSet.id} sx={{ mb: 1 }}>
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                      backgroundColor: 'info.light',
                                      '&:hover': { backgroundColor: 'info.main' },
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                      <AdUnitsIcon />
                                      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                        {adSet.name}
                                      </Typography>
                                      <Chip label={adSet.status} color={getStatusColor(adSet.status)} size="small" />
                                      {adSet.optimization_goal && (
                                        <Typography variant="body2" color="text.secondary">
                                          {adSet.optimization_goal}
                                        </Typography>
                                      )}
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Box sx={{ pl: 2 }}>
                                      <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Detalles del Conjunto de Anuncios
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                            gap: 1,
                                          }}
                                        >
                                          <Typography variant="body2">
                                            <strong>Presupuesto Diario:</strong>{' '}
                                            {formatCurrency(adSet.daily_budget, adAccount.currency)}
                                          </Typography>
                                          <Typography variant="body2">
                                            <strong>Presupuesto Total:</strong>{' '}
                                            {formatCurrency(adSet.lifetime_budget, adAccount.currency)}
                                          </Typography>
                                          <Typography variant="body2">
                                            <strong>Evento de Facturación:</strong> {adSet.billing_event || 'N/A'}
                                          </Typography>
                                          <Typography variant="body2">
                                            <strong>Monto de Puja:</strong>{' '}
                                            {formatCurrency(adSet.bid_amount, adAccount.currency)}
                                          </Typography>
                                        </Box>
                                      </Box>

                                      {adSet.ads.length === 0 ? (
                                        <Typography color="text.secondary">No hay anuncios en este conjunto</Typography>
                                      ) : (
                                        <Box>
                                          <Typography variant="subtitle2" gutterBottom>
                                            Anuncios ({adSet.ads.length})
                                          </Typography>
                                          <Box sx={{ display: 'grid', gap: 1 }}>
                                            {adSet.ads.map((ad) => (
                                              <Card key={ad.id} variant="outlined" sx={{ backgroundColor: 'grey.100' }}>
                                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <ImageIcon />
                                                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                                                      {ad.name}
                                                    </Typography>
                                                    <Chip
                                                      label={ad.status}
                                                      color={getStatusColor(ad.status)}
                                                      size="small"
                                                    />
                                                  </Box>
                                                  {ad.lastSyncAt && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                                      Última sincronización: {formatDate(ad.lastSyncAt)}
                                                    </Typography>
                                                  )}
                                                </CardContent>
                                              </Card>
                                            ))}
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  </AccordionDetails>
                                </Accordion>
                              ))
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Última actualización: {formatDate(hierarchyData.retrievedAt)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ZellerAdsManager;
